import { X } from "lucide-react";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDismissAnecdoteBanner } from "../hooks/useAnecdotes";
import { selectUser, setUser } from "../store/slices/auth";
import { dispatch, useSelector } from "../store/store";

type Props = {
  position?: "top" | "bottom" | "left" | "right";
  children: React.ReactNode;
};

export const AnecdoteTooltip: FC<Props> = ({
  children,
  position = "bottom",
}) => {
  const { t } = useTranslation();
  const user = useSelector(selectUser);
  const dismissMutation = useDismissAnecdoteBanner();

  // Local state to hide tooltip temporarily (for this session)
  const [isHiddenTemporarily, setIsHiddenTemporarily] = useState(false);

  // Don't show if user has permanently dismissed or temporarily hidden
  const isVisible = !user?.dismissed_anecdote_banner && !isHiddenTemporarily;

  const getPositionClasses = () => {
    switch (position) {
      case "top":
        return "absolute left-1/2 transform -translate-x-1/2 bottom-full mb-2";
      case "bottom":
        return "absolute left-1/2 transform -translate-x-1/2 top-full mt-2";
      case "left":
        return "absolute right-full top-1/2 transform -translate-y-1/2 mr-2";
      case "right":
        return "absolute left-full top-1/2 transform -translate-y-1/2 ml-2";
      default:
        return "absolute left-1/2 transform -translate-x-1/2 top-full mt-2";
    }
  };

  const handleClose = () => {
    setIsHiddenTemporarily(true);
  };

  const handleDismissPermanently = () => {
    dismissMutation.mutateAsync(null).then(() => {
      // Update the user in the store
      if (user) {
        dispatch(
          setUser({ user: { ...user, dismissed_anecdote_banner: true } })
        );
      }
    });
    setIsHiddenTemporarily(true);
  };

  return (
    <div className="relative">
      {children}
      {isVisible && (
        <div
          className={[
            getPositionClasses(),
            "w-64 bg-herb-500 text-white text-xs rounded-lg py-2 px-3 z-50 shadow-lg",
          ].join(" ")}
        >
          <div className="flex justify-between items-start gap-2">
            <p className="font-nunito text-sm leading-relaxed">
              {t("anecdotes.tooltip.message")}
            </p>
            <button
              onClick={handleClose}
              className="text-white/70 hover:text-white transition-colors flex-shrink-0"
              aria-label={t("common.close")}
            >
              <X size={16} />
            </button>
          </div>
          <div className="mt-2 pt-2 border-t border-white/20">
            <label className="flex items-center gap-2 cursor-pointer text-xs text-white/80 hover:text-white transition-colors">
              <input
                type="checkbox"
                onChange={handleDismissPermanently}
                className="w-3 h-3 rounded border-white/50 text-herb-600 focus:ring-herb-500 focus:ring-offset-0"
              />
              <span>{t("anecdotes.tooltip.dontShowAgain")}</span>
            </label>
          </div>
          {/* Arrow */}
          {position === "bottom" && (
            <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-8 border-r-8 border-b-8 border-l-transparent border-r-transparent border-b-herb-500" />
          )}
          {position === "top" && (
            <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-herb-500" />
          )}
        </div>
      )}
    </div>
  );
};
